/**
 * CP-96: Detail modules (Ship Information, Reviews) show only real data.
 *
 * The module is hidden unless the payload carries at least one of: a spec
 * (year built, capacity, tonnage, length, decks, crew), a real description,
 * a real amenity, or a deck plan URL. Three kinds of filler are dropped:
 *   - the generic "<ship> is a vessel offering amenities and entertainment"
 *     blurb (and empty / name-only descriptions),
 *   - cabin categories (Interior, Outside View, Balcony, Suite, ...) stored
 *     in the amenities list by the scraper,
 *   - zero / blank spec values.
 * Nothing here writes copy. It only decides what real data to show.
 */

const BOILERPLATE_BLURBS = [
  /\bis a (?:cruise )?(?:ship|vessel) offering amenities and entertainment\b/i,
  /^(?:n\/a|na|none|null|tbd|unknown|no description(?: available)?|description (?:coming soon|not available))\.?$/i,
];

// A cabin category always names one of these.
const CABIN_CORE = new Set([
  'interior', 'inside', 'outside', 'oceanview', 'ocean-view', 'balcony', 'balconies',
  'veranda', 'verandah', 'suite', 'suites', 'mini-suite', 'minisuite', 'stateroom',
  'staterooms', 'cabin', 'cabins', 'studio', 'studios', 'penthouse', 'haven',
]);

// Words that only qualify a cabin category.
const CABIN_MODIFIERS = new Set([
  'the', 'a', 'an', 'and', 'with', 'of', '&', 'view', 'ocean', 'sea', 'window',
  'picture', 'porthole', 'obstructed', 'partial', 'partially', 'virtual', 'mini',
  'junior', 'grand', 'royal', 'owner', "owner's", 'owners', 'family', 'deluxe',
  'premium', 'superior', 'standard', 'classic', 'club', 'concierge', 'aqua',
  'aquaclass', 'class', 'spa', 'solo', 'single', 'large', 'spacious', 'signature',
  'sky', 'vista', 'neptune', 'pinnacle', 'terrace', 'loft', 'villa', 'courtyard',
  'room', 'rooms', 'category', 'guarantee', 'guaranteed', 'accessible', 'connecting',
]);

const SPEC_FIELDS = ['year_built', 'capacity', 'tonnage', 'length', 'decks', 'crew'];

function cleanText(value) {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
}

/** True for empty, name-only, or generic filler ship descriptions. */
export function isBoilerplateShipBlurb(description, shipName) {
  const text = cleanText(description);
  if (!text) return true;
  const name = cleanText(shipName).toLowerCase();
  const bare = text.toLowerCase().replace(/[.!]+$/, '');
  if (name && (bare === name || bare === `the ${name}`)) return true;
  return BOILERPLATE_BLURBS.some((pattern) => pattern.test(text));
}

/** True when an "amenity" is really a cabin category (e.g. "Outside View"). */
export function isCabinTypeLabel(label) {
  const text = cleanText(label).toLowerCase()
    // "Ocean View" / "Sea View" and Celebrity "Concierge Class" / "AquaClass"
    // name cabin categories without a core cabin word.
    .replace(/\b(?:ocean|sea|outside|partial|obstructed)[\s-]+view\b/g, 'oceanview')
    .replace(/\b(?:concierge|aqua)[\s-]?class\b/g, 'stateroom');
  if (!text) return true;
  const tokens = text.split(/[\s/,()]+/).filter(Boolean);
  if (!tokens.length) return true;
  let hasCore = false;
  for (const token of tokens) {
    const word = token.replace(/^[^a-z0-9&']+|[^a-z0-9&']+$/g, '');
    if (!word) continue;
    if (CABIN_CORE.has(word)) {
      hasCore = true;
    } else if (!CABIN_MODIFIERS.has(word)) {
      return false;
    }
  }
  return hasCore;
}

/** Real amenities only; cabin categories and blanks are dropped. */
export function realShipAmenities(amenities) {
  if (!Array.isArray(amenities)) return [];
  const seen = new Set();
  const out = [];
  for (const item of amenities) {
    const text = cleanText(item);
    if (!text || isCabinTypeLabel(text)) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

function positiveNumber(value) {
  if (value == null || value === '' || typeof value === 'boolean') return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

/**
 * Cleaned ship profile. Filler fields come back null / empty so the template
 * can gate each block on presence alone.
 */
export function shipProfile(info, shipName) {
  if (!info || typeof info !== 'object' || Array.isArray(info)) return null;
  const name = cleanText(info.name) || cleanText(shipName);
  const description = isBoilerplateShipBlurb(info.description, name)
    ? null
    : cleanText(info.description);
  const specs = {};
  for (const field of SPEC_FIELDS) {
    specs[field] = positiveNumber(info[field]);
  }
  const deckPlanUrl = cleanText(info.deck_plan_url) || null;
  return {
    name,
    cruise_line: cleanText(info.cruise_line),
    description,
    specs,
    hasSpecs: SPEC_FIELDS.some((field) => specs[field] !== null),
    amenities: realShipAmenities(info.amenities),
    deck_plan_url: deckPlanUrl,
  };
}

/** Show the Ship Information module only with at least one real fact. */
export function hasShipProfile(info, shipName) {
  const profile = shipProfile(info, shipName);
  if (!profile) return false;
  return Boolean(
    profile.description
    || profile.hasSpecs
    || profile.amenities.length
    || profile.deck_plan_url
  );
}

/** Reviews (with summary and filter/sort bar) show only when reviews exist. */
export function hasReviews(summary) {
  if (!summary || typeof summary !== 'object') return false;
  const total = Number(summary.total_reviews);
  return Number.isFinite(total) && total > 0;
}
